/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const chartData = [
  { name: 'Jan', events: 4, registrations: 12 },
  { name: 'Feb', events: 3, registrations: 8 },
  { name: 'Mar', events: 6, registrations: 25 },
  { name: 'Apr', events: 2, registrations: 15 },
  { name: 'May', events: 5, registrations: 18 },
  { name: 'Jun', events: 7, registrations: 30 },
];

const StatCard = ({ title, value, icon, link, trend, trendLabel, accentColor }) => (
  <div className="stat-card-modern">
    <Link to={link || '#'} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <div className="stat-card-body">
        <div className="stat-card-icon" style={{ background: `${accentColor}18`, color: accentColor }}>
          <FeatherIcon icon={icon} size={22} />
        </div>
        <div className="stat-card-info">
          <span className="stat-card-label">{title}</span>
          <h3 className="stat-card-value">{value}</h3>
        </div>
        {trend !== undefined && (
          <div className={`stat-card-trend ${trend >= 0 ? 'positive' : 'negative'}`}>
            <FeatherIcon icon={trend >= 0 ? 'trending-up' : 'trending-down'} size={14} />
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
    </Link>
  </div>
);

const AdminLandingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    events: 6, registrations: 1, gallery: 0, news: 0,
    subscribers: 0, team: 0, contactRequests: 2, usefulLinks: 0,
    services: 3, importantInfo: 0, carousel: 2
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="page-wrapper" style={{ background: '#f8f9fa', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="content container-fluid p-4">

        {/* Header Section */}
        <div className="page-header mb-4">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title fw-bold" style={{ color: '#1a1a2e', fontSize: '28px' }}>Dashboard Overview</h3>
              <p className="text-muted mb-0">Monitor your upcoming events, registrations, and site performance.</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="row">
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="col-xl-3 col-sm-6 col-12 mb-4">
                <div className="card border-0 shadow-sm" style={{ height: '120px', borderRadius: '16px' }}>
                  <div className="card-body d-flex align-items-center justify-content-center placeholder-glow">
                    <span className="placeholder col-8"></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* KPI Cards Row */}
            <div className="row mb-4">
              <div className="col-xl-3 col-sm-6 col-12 mb-4">
                <StatCard title="Total Events" value={stats.events} icon="calendar" link="/informationcard" trend={12} trendLabel="vs last month" accentColor="#ed8936" />
              </div>
              <div className="col-xl-3 col-sm-6 col-12 mb-4">
                <StatCard title="Registrations" value={stats.registrations} icon="users" link="/event-registrations" trend={24} trendLabel="vs last month" accentColor="#48bb78" />
              </div>
              <div className="col-xl-3 col-sm-6 col-12 mb-4">
                <StatCard title="News Articles" value={stats.news} icon="file-text" link="/blogview" trend={-5} trendLabel="vs last month" accentColor="#4299e1" />
              </div>
              <div className="col-xl-3 col-sm-6 col-12 mb-4">
                <StatCard title="Subscribers" value={stats.subscribers} icon="mail" link="/subscribers" trend={8} trendLabel="vs last month" accentColor="#9f7aea" />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="quick-actions-bar">
                  <h6 className="quick-actions-title">Quick Actions</h6>
                  <div className="quick-actions-grid">
                    <Link to="/informationcard" className="quick-action-btn">
                      <FeatherIcon icon="plus-circle" size={18} />
                      <span>New Event</span>
                    </Link>
                    <Link to="/blogview" className="quick-action-btn">
                      <FeatherIcon icon="edit-3" size={18} />
                      <span>Write News</span>
                    </Link>
                    <Link to="/galleryheader" className="quick-action-btn">
                      <FeatherIcon icon="upload" size={18} />
                      <span>Upload to Gallery</span>
                    </Link>
                    <Link to="/contactsubmissions" className="quick-action-btn">
                      <FeatherIcon icon="inbox" size={18} />
                      <span>View Messages</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="row mb-4">
              <div className="col-12 col-xl-8 mb-4">
                <div className="card shadow-sm h-100" style={{ borderRadius: '16px', border: 'none', overflow: 'hidden' }}>
                  <div className="card-header border-0 pt-4 pb-0 px-4 d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #D4A853, #C49A3C)' }}>
                    <h5 className="card-title fw-bold mb-0 text-white">Engagement Overview</h5>
                  </div>
                  <div className="card-body p-4" style={{ height: '350px', background: 'linear-gradient(135deg, #D4A853, #C49A3C)' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorReg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ffffff" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#ffffff" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#FFF8E7" stopOpacity={0.6} />
                            <stop offset="95%" stopColor="#FFF8E7" stopOpacity={0.1} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#FFF8E7', fontSize: 12 }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#FFF8E7', fontSize: 12 }} dx={-10} />
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.15)" />
                        <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', background: '#ffffff', color: '#2d3748' }} />
                        <Area type="monotone" dataKey="registrations" stroke="#ffffff" strokeWidth={3} fillOpacity={1} fill="url(#colorReg)" />
                        <Area type="monotone" dataKey="events" stroke="#FFF8E7" strokeWidth={3} fillOpacity={1} fill="url(#colorEvents)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* System Data */}
              <div className="col-12 col-xl-4 mb-4">
                <div className="card shadow-sm h-100" style={{ borderRadius: '16px', border: 'none' }}>
                  <div className="card-header bg-white border-0 pt-4 pb-0 px-4">
                    <h5 className="card-title fw-bold mb-0 text-dark">System Data</h5>
                  </div>
                  <div className="card-body p-4">
                    <div className="d-flex flex-column gap-3">
                      {[
                        { title: 'Gallery Images', value: stats.gallery, icon: 'image', link: '/galleryheader', color: '#4299e1', bg: '#ebf8ff' },
                        { title: 'Team Members', value: stats.team, icon: 'users', link: '/teamheader', color: '#48bb78', bg: '#f0fff4' },
                        { title: 'Contact Requests', value: stats.contactRequests, icon: 'message-circle', link: '/contactsubmissions', color: '#ed8936', bg: '#fff4e6' },
                        { title: 'Partners', value: stats.services, icon: 'award', link: '/serviceheader', color: '#9f7aea', bg: '#faf5ff' },
                        { title: 'Carousel Items', value: stats.carousel, icon: 'monitor', link: '/herocarousel', color: '#f56565', bg: '#fff5f5' },
                      ].map((item, idx) => (
                        <Link to={item.link} key={idx} className="text-decoration-none text-dark">
                          <div className="d-flex align-items-center justify-content-between p-3 rounded hover-effect" style={{ background: '#f8f9fa', transition: 'all 0.2s', border: '1px solid #edf2f7', borderRadius: '12px' }}>
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ color: item.color, background: item.bg, padding: '10px', borderRadius: '10px' }}><FeatherIcon icon={item.icon} size={20} /></div>
                              <span className="fw-medium" style={{ color: '#4A4A5A', fontSize: '14px' }}>{item.title}</span>
                            </div>
                            <span className="badge text-dark bg-white border px-3 py-2 rounded-pill" style={{ fontSize: '14px', fontWeight: 700 }}>{item.value}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <style>{`
              .hover-effect:hover {
                background: #fff !important;
                box-shadow: 0 4px 15px rgba(0,0,0,0.05);
                transform: translateY(-2px);
              }
              .bg-soft-success { background: rgba(25, 135, 84, 0.1); }
              .bg-soft-danger { background: rgba(220, 53, 69, 0.1); }
            `}</style>

          </>
        )}
      </div>
    </div>
  );
};

export default AdminLandingDashboard;
